import { Dialog, DialogActions, DialogContent, Grid, InputAdornment, Typography } from '@mui/material'
import { MDButton } from '../../../../Component/Common/Components/MDButton'
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreateOfoCodeCategoryValidationSchema } from './CreateOfoCodeCategoryValidationSchema';
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { LoadingButton } from '@mui/lab';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { OfoCodeAtom, ofoCodeSelector } from '../../../../State/OfoCode.Atom';
import { useEffect, useState } from 'react';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { OfoCodeRequest } from '../../../../Component/Model/Dto/WSP/OfoCode';
import { toast } from 'react-toastify';
import { OfoCodeService } from '../../../../Component/Api/OFOCodes/OfoCodeService';
import { SmallLoading } from '../../../../Component/Common/Components/Loading/IntermediateLoading';
import { WSPScorecardAtom } from '../../../../State/WSPScorecard.Atom';
import { getIOptionsArrayWithInfo } from '../../../../Helpers/DropdownWithDescriptionHelper';
import AppSelectListWithInfo from '../../../../Component/Common/Components/FormValidationInputs/AppSelectListWithInfo';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';

interface IProps {
  open: boolean;
  closeModal: () => void;
  jobTitleId: number;
  organisationId: number;
}
export const CreateOfoCodeCategory = ({ open, closeModal, jobTitleId, organisationId }: IProps) => {
  const categoryId = "categoryId";
  const categoryName = "categoryName";
  const categoryDescription = "categoryDescription";
  const ofoCodeData = useRecoilValue(ofoCodeSelector);
  const setOfoCodes = useSetRecoilState(OfoCodeAtom);
  const wspScorecard = useRecoilValue(WSPScorecardAtom);
  const { control, handleSubmit, watch, reset, setValue, formState: { isSubmitting } } = useForm({
    mode: 'all',
    resolver: yupResolver(CreateOfoCodeCategoryValidationSchema)
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const selectedMajor = watch('majorGroup');
  const selectedSubMajor = watch('subMajorGroup');
  const selectedMinorGroup = watch('minorGroup');
  const selectedUnitGroup = watch('unitGroup');
  const selectedOccupationGroup = watch('occupationGroup');

  const subMajorOptions = getIOptionsArrayWithInfo(
    ofoCodeData?.majors.find(major => major.categoryId === selectedMajor)?.children ?? [],
    categoryId,
    categoryName,
    categoryDescription
  );

  const selectedSubMajorData = ofoCodeData?.majors
    .find(major => major.categoryId === selectedMajor)
    ?.children.find(subMajor => subMajor.categoryId === selectedSubMajor);

  const minorGroupOptions = getIOptionsArrayWithInfo(
    selectedSubMajorData?.children ?? [],
    categoryId,
    categoryName,
    categoryDescription
  );

  const minorGroupData = selectedSubMajorData?.children.find(
    minorGroup => minorGroup.categoryId === selectedMinorGroup
  );

  const unitGroupOptions = getIOptionsArrayWithInfo(
    minorGroupData?.children ?? [],
    categoryId,
    categoryName,
    categoryDescription
  );

  const selectedUnitGroupData = minorGroupData?.children.find(
    unitGroup => unitGroup.categoryId === selectedUnitGroup
  );

  const occupationGroupOptions = getIOptionsArrayWithInfo(
    selectedUnitGroupData?.children ?? [],
    categoryId,
    categoryName,
    categoryDescription
  );

  const getOfoRecord = async () => {
    try {
      setIsLoading(true);
      const result = await OfoCodeService.getJobById(jobTitleId, wspScorecard.wspScorecardId);
      if (result.data) {
        updateDropdownValues(result.data.categoryId.toString());
        setValue('jobTitle', result.data.jobTitle);
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (ofoCodeData === null) {
      getOfoCodes();
    }
  }, [])

  useEffect(() => {
    reset();
    if (jobTitleId !== 0) {
      getOfoRecord();
    }
  }, [jobTitleId])

  const getOfoCodes = async () => {
    try {
      const response = await OfoCodeService.getAllOfoCodes();
      setOfoCodes(response.data);
    } catch (error) {
      console.error(error)
    }
  }

  const handleOfoCodeChange = (event: any) => {
    const regex = /(\d+)/;
    const match = event.target.value.match(regex);
    if (match !== null) {
      if (match[1].length === 6) {
        const ofoData = match[1];
        updateDropdownValues(ofoData);
      }
    };
  }

  const updateDropdownValues = (ofoData: string) => {
    const majorGroupMatch = ofoData.substring(0, 1);
    setValue('majorGroup', +majorGroupMatch, { shouldValidate: true });

    const subMajorGroupMatch = ofoData.substring(0, 2);
    setValue('subMajorGroup', +subMajorGroupMatch, { shouldValidate: true });

    const minorGroupMatch = ofoData.substring(0, 3);
    setValue('minorGroup', +minorGroupMatch, { shouldValidate: true });

    const unitGroupMatch = ofoData.substring(0, 4);
    setValue('unitGroup', +unitGroupMatch, { shouldValidate: true });

    const occupationGroupMatch = ofoData.substring(0, 6);
    setValue('occupationGroup', +occupationGroupMatch, { shouldValidate: true });

  }

  const close = () => {
    closeModal();
  }

  const handleDataSubmit = async (data: FieldValues) => {
    const request: OfoCodeRequest = {
      organisationId: organisationId,
      categoryId: data.occupationGroup,
      jobTitle: data.jobTitle,
      scorecardId: wspScorecard.wspScorecardId
    }
    if (occupationGroupOptions.length === 0) {
      toast.error('OFO Code does not exist')
    }
    else {
      try {
        if (jobTitleId === 0) {
          await createJob(request);
        } else {
          await updateJob(jobTitleId, request);
        }
      } catch (error) {
        toast.error('Failed to Create OFO Code categorisation')
        console.log(error);
      } finally {
        close();
      }
    }
  }

  const createJob = async (request: any) => {
    const result = await OfoCodeService.createOfoJob(request);
    if (result.data) {
      toast.success('Successfully created OFO Code categorisation');
    }
  }

  const updateJob = async (jobTitleId: number, request: any) => {
    const result = await OfoCodeService.updateOfoJob(jobTitleId, request);
    if (result.data) {
      toast.success('Successfully updated OFO Code categorisation record');

    }
  }

  if (selectedOccupationGroup !== undefined) {
    setValue('OfoCode', selectedOccupationGroup ? `${selectedOccupationGroup}` : '');
  }

  if (isLoading) {
    return (
      <Dialog open={open} onClose={() => close()} maxWidth={false} aria-labelledby="form-dialog-title">
        <CustomDialogTitle id="assign-dialog-title" onClose={() => close()}>
          Edit OFO Code Categorisation
        </CustomDialogTitle>
        <DialogContent>
          <SmallLoading />
        </DialogContent>
      </Dialog>
    )
  }
  return (
    <Dialog open={open} onClose={() => close()} maxWidth={false} aria-labelledby="form-dialog-title">
      <CustomDialogTitle id="assign-dialog-title" onClose={() => close()}>
        {jobTitleId > 0 ? 'Edit' : 'Add'} OFO Code Categorisation

      </CustomDialogTitle>
      <DialogContent sx={{ paddingTop: "5px !important", width: "600px " }}>

        <form id='OFOCodeForm' onSubmit={handleSubmit(handleDataSubmit)}>
          <Typography pb={2} variant="body2">You may either filter the OFO codes using the group dropdowns or search for an OFO code.</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12}>
              <AppTextInput label='Job Title' name='jobTitle' control={control} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectListWithInfo
                control={control}
                name="majorGroup"
                label="Major Group"
                items={getIOptionsArrayWithInfo(ofoCodeData?.majors ?? [], categoryId, categoryName, categoryDescription)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectListWithInfo
                control={control}
                name="subMajorGroup"
                label="Sub-Major Group"
                items={subMajorOptions}
                disabled={subMajorOptions.length === 0}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectListWithInfo
                control={control}
                name="minorGroup"
                label="Minor Group"
                items={minorGroupOptions}
                disabled={minorGroupOptions.length === 0}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectListWithInfo
                control={control}
                name="unitGroup"
                label="Unit Group"
                items={unitGroupOptions}
                disabled={unitGroupOptions.length === 0}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <AppSelectListWithInfo
                control={control}
                name="occupationGroup"
                label="Occupation"
                items={occupationGroupOptions}
                disabled={occupationGroupOptions.length === 0}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AppTextInput label='Ofo Code' name='OfoCode' control={control} onChange={handleOfoCodeChange} InputProps={{ startAdornment: <InputAdornment position="start" ><MDTypography sx={{ fontSize: '0.85rem' }}>2021 - </MDTypography></InputAdornment> }} />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={isSubmitting} variant='contained' form='OFOCodeForm' type="submit" color='primary'>Submit</LoadingButton>
        <MDButton variant='outlined' color='primary' onClick={() => close()}>Cancel</MDButton>
      </DialogActions>
    </Dialog>
  )
}

